import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Stack,
  Container,
  Button,
  TextField,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Collapse,
  alpha,
  useMediaQuery,
  Autocomplete,
} from '@mui/material';
import {
  Launch as LaunchIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Event as EventIcon,
  Group as GroupIcon,
  Send as SendIcon,
  List as ListIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  PersonAdd as FollowIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Comment as CommentIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  Science as ScienceIcon,
  Description as DescriptionIcon,
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon,
  TravelExplore as DiscoverIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import GuideTour, { useGuideTour } from 'components/common/guide/GuideTour';

import ConsortiumDialog from '../../common/dialogs/ConsortiumDialog';
import useFeedAPI from '../../../shared/hooks/useFeedAPI';
import { styled, useTheme } from '@mui/material/styles';
import BackButton from '../../common/buttons/BackButton';
import MeetingScheduleDialog from '../../common/dialogs/MeetingScheduleDialog';
import { axiosService, useUser }  from 'shared/helpers/axios'; 
import { Comments as CallComments } from 'components/common/Comments';
import LikeButton from 'components/common/buttons/LikeButton';
import InviteToSuggestEntityDialog from 'components/common/dialogs/InviteToSuggestEntityDialog';
import FollowButton from 'components/common/buttons/FollowButton';
import EntityLink from 'components/common/links/EntityLink';
import ReportButton from 'components/common/ReportButton';
import { ProfileDateTimePicker } from 'components/social/profile/ProfileDialogs';
import { formatCompactMoney } from 'shared/helpers/money';
import CallText, { introOnly } from './CallText';

// Calls kept up to date by the automatic sync (backend funding/call/sync/)
const SYNC_SOURCES = {
  ec_ft: 'the EU Funding & Tenders Portal',
  grants_gov: 'Grants.gov',
  ukri: 'UK Research and Innovation',
};

const Header = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
  borderRadius: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  backdropFilter: 'blur(8px)',
}));

const SidebarCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '16px',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.5)} 100%)`,
  '&:hover': {
    // transform: 'translateY(-2px)',
    // boxShadow: `0 12px 24 px ${alpha(theme.palette.common.black, 0.1)}`,
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
}));


const InfoCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '20px',
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.light, 0.15)} 100%)`,
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  // boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
  backdropFilter: 'blur(16px)',
  // border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
}));




const GradientDivider = styled(Divider)(({ theme }) => ({
  background: `linear-gradient(90deg, transparent, ${theme.palette.divider}, transparent)`,
  height: 1,
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: '12px',
  fontWeight: 500,
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  },
}));

// Helper function to get action type display name
const getActionTypeDisplay = (code) => {
  const actionTypes = {
    'research': 'Research',
    'innovation': 'Innovation',
    'ria': 'Research and Innovation',
    'both': 'Research and Innovation',
    'csa': 'Coordination and Support Action',
    'cofund': 'Co-Fund Action',
    'ppi': 'Public Procurement of Innovation',
  };
  return actionTypes[code] || code;
};

const CALL_DETAIL_GUIDE_STEPS = [
  {
    label: 'Explore',
    title: 'Dive into the call sections',
    description: 'Expand for scope, expected outcomes, eligibility, categories, and required skills.',
    target: 'sections',
  },
  {
    label: 'Act',
    title: 'Express your interest with a comment',
    description: "Tell the community whether you're seeking a consortium or partners — it's the fastest way to get noticed.",
    target: 'comment',
  },
];

const CallDetailPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { callId } = useParams();
  const { user: currentUser } =  useUser();
  const location = useLocation();
  const { selectedAgency, selectedProgram, selectedCall } = location.state || {};
  
  const [commentCount, setCommentCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsExpanded, setCommentsExpanded] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [commentTypeOfInterest, setCommentTypeOfInterest] = useState(0);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const sectionsRef = useRef(null);
  const commentComposerRef = useRef(null);
  const guide = useGuideTour({
    storageKey: 'call-detail.guide.dismissed.v1',
    steps: CALL_DETAIL_GUIDE_STEPS,
    targetRefs: { sections: sectionsRef, comment: commentComposerRef },
  });

  let nSuggestionsToFollow = 5;

  const { fetchSuggestionsToFollow } = useFeedAPI();

  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);
  const [consortiumDialogOpen, setConsortiumDialogOpen] = useState(false);
  const [potentialMembers, setPotentialMembers] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [suggestionsToFollow, setSuggestionsToFollow] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [suggestedExperts, setSuggestedExperts] = useState([]);
  const [loadingExperts, setLoadingExperts] = useState(false);

  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const StepPill = ({ icon, label, active }) => (
    <StyledChip 
      icon={icon} 
      label={label} 
      variant={active ? 'filled' : 'outlined'} 
      color={active ? 'primary' : 'default'} 
      sx={{ 
        mr: 1, 
        mb: { xs: 1, sm: 0 },
        '& .MuiChip-icon': { color: active ? 'inherit' : 'primary.main' }
      }} 
    />
  );

  const LS_KEY = 'funding.quickLinks.v3';
  const currentCallId = selectedCall?.id || callId;
  const [isPinned, setIsPinned] = React.useState(() => {
    try {
      const existing = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
      return existing.some(l => l.id === currentCallId && l.type === 'call');
    } catch { return false; }
  });

  React.useEffect(() => {
    const onStorage = () => {
      try {
        const existing = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
        setIsPinned(existing.some(l => l.id === currentCallId && l.type === 'call'));
      } catch { /* ignore */ }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [currentCallId]);

  const handlePinCall = () => {
    try {
      const existing = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
      const newLink = {
        id: currentCallId,
        type: 'call',
        title: selectedCall?.title || call?.title || 'Call',
        subtitle: selectedProgram?.name || selectedAgency?.name || 'Call',
      };
      const exists = existing.some(l => l.id === newLink.id && l.type === newLink.type);
      if (!exists) {
        localStorage.setItem(LS_KEY, JSON.stringify([newLink, ...existing]));
        window.dispatchEvent(new Event('storage'));
        setIsPinned(true);
      }
    } catch (e) {
      console.error('Failed to pin call', e);
    }
  };

  const stepChips = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
      <StepPill icon={<BusinessIcon />} label={selectedAgency?.name || 'Select Agency'} active={true} />
      <StepPill icon={<CategoryIcon />} label={selectedProgram?.name || 'Choose Program'} active={true} />
      <StepPill icon={<ListIcon />} label={selectedCall?.code || 'Call Details'} active={true} />
    </Box>
  );

  const fetchSuggestionsToFollowData = useCallback(async () => {
    setLoadingSuggestions(true);
    try {
      const suggestions = await fetchSuggestionsToFollow(nSuggestionsToFollow);
      setSuggestionsToFollow(suggestions || []);
    } catch (e) {
      console.error('Error fetching suggestions to follow:', e);
      setSuggestionsToFollow([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [fetchSuggestionsToFollow]);

  const fetchSuggestedExperts = useCallback(async () => {
    if (!callId) return;
    setLoadingExperts(true);
    try {
      const response = await axiosService.get(`funding/calls/${callId}/suggested_experts/`);
      setSuggestedExperts(
        response.data
          .filter((expert) => expert.id !== currentUser.id) || []
      );


    } catch (e) {
      console.error('Error fetching suggested experts:', e);
      setSuggestedExperts([]);
    } finally {
      setLoadingExperts(false);
    }
  }, [callId]);



  useEffect(() => {
    if (!suggestionsToFollow.length) {
      fetchSuggestionsToFollowData();
    }
  }, [fetchSuggestionsToFollowData, suggestionsToFollow.length]);

  useEffect(() => {
    if (callId) {
      fetchSuggestedExperts();
    }
  }, [callId, fetchSuggestedExperts]);

  useEffect(() => {
    fetchCallDetails();
    fetchComments();
  }, [callId]);

  useEffect(() => {
    if (comments.length > 0 && window.location.hash) {
      const hash = window.location.hash;
      if (hash.startsWith('#comment-')) {
        setCommentsExpanded(true);
        setTimeout(() => {
          const element = document.querySelector(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.style.transition = 'background-color 0.5s';
            element.style.backgroundColor = alpha(theme.palette.primary.main, 0.1);
            setTimeout(() => {
              element.style.backgroundColor = 'transparent';
            }, 2000);
          }
        }, 100);
      }
    }
  }, [comments, theme.palette.primary.main]);

  const sanitizedCallId = callId?.toString().replace(/\/+$/, '');

  const fetchCallDetails = async () => {
    if (!sanitizedCallId) {
      setError('Invalid funding call ID.');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosService.get(`/funding/calls/${sanitizedCallId}/`);
      setCall(response.data);
      // no introduction of its own: show the scope / outcomes straight away instead of an empty summary
      if (!introOnly(response.data.description, response.data)) setDetailsExpanded(true);
    } catch (err) {
      console.error('Error fetching call details:', err);
      setError(
        err?.response?.data?.detail || err?.response?.data?.error || 'Failed to load call details. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditCall = async (editedData) => {
    try {
      await axiosService.patch(`/funding/calls/${callId}/`, editedData);
      // Re-fetch the full call details to get properly expanded relationships
      await fetchCallDetails();
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Error updating call:', err);
      throw err;
    }
  };

  const fetchPotentialConsortiumMembers = async () => {
    try {
      const params = new URLSearchParams();
      if (call?.type_of_action) {
        params.append('search', call.type_of_action);
      }
      if (call?.expertise_areas && call.expertise_areas.length > 0) {
        const expertiseQuery = call.expertise_areas.join(' ');
        params.append('search', expertiseQuery);
      }
      params.append('page_size', '25');
      const response = await axiosService.get(`/users/?${params.toString()}`);
      const users = response.data.results || response.data || [];
      return users.map(user => ({
        ...user,
        consortiumFitScore: calculateConsortiumFit(user, call),
        suggestedRole: suggestUserRole(user, call)
      })).sort((a, b) => b.consortiumFitScore - a.consortiumFitScore).slice(0, 15);
    } catch (err) {
      console.error('Error fetching potential consortium members:', err);
      return [];
    }
  };

  const calculateUserRelevance = (user, call) => {
    let score = 0.5;
    if (!user || !call) return score;
    if (user.expertise && call.expertise_areas) {
      const userExpertise = user.expertise.toLowerCase();
      const callExpertise = call.expertise_areas.join(' ').toLowerCase();
      const matches = callExpertise.split(' ').filter(keyword => 
        keyword.length > 3 && userExpertise.includes(keyword)
      ).length;
      score += matches * 0.1;
    }
    if (user.position) {
      const position = user.position.toLowerCase();
      if (position.includes('professor') || position.includes('researcher')) score += 0.2;
      if (position.includes('senior') || position.includes('lead')) score += 0.1;
    }
    if (user.active_projects_count > 0) score += 0.1;
    return Math.min(score, 1.0);
  };

  const calculateConsortiumFit = (user, call) => {
    let score = 0.6;
    if (!user || !call) return score;
    if (user.expertise && call.expertise_areas) {
      const userExpertise = user.expertise.toLowerCase();
      const callExpertise = call.expertise_areas.join(' ').toLowerCase();
      const matches = callExpertise.split(' ').filter(keyword => 
        keyword.length > 3 && userExpertise.includes(keyword)
      ).length;
      score += matches * 0.15;
    }
    if (user.position) {
      const position = user.position.toLowerCase();
      if (position.includes('professor') || position.includes('director')) score += 0.2;
      if (position.includes('lead') || position.includes('head')) score += 0.15;
    }
    if (user.active_projects_count > 2) score += 0.15;
    return Math.min(score, 1.0);
  };

  const suggestUserRole = (user, call) => {
    if (!user.position) return 'Research Partner';
    const position = user.position.toLowerCase();
    if (position.includes('professor') || position.includes('director')) {
      return 'Principal Investigator';
    } else if (position.includes('senior') || position.includes('lead')) {
      return 'Technical Lead';
    } else if (position.includes('manager') || position.includes('coordinator')) {
      return 'Project Coordinator';
    } else if (position.includes('researcher') || position.includes('scientist')) {
      return 'Research Partner';
    } else {
      return 'Collaborator';
    }
  };

  const handleSuggestClick = (e) => {
    e.preventDefault();
    setSuggestDialogOpen(true);
  };

  const handleConsortiumClick = async () => {
    setLoadingRecommendations(true);
    try {
      const members = await fetchPotentialConsortiumMembers();
      setPotentialMembers(members);
    } catch (err) {
      console.warn('Error fetching potential consortium members:', err);
      setPotentialMembers([]);
    } finally {
      setLoadingRecommendations(false);
      setConsortiumDialogOpen(true);
    }
  };

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const response = await axiosService.get('/interact/comments/', {
        params: {
          target_type: 'call',
          target_id: callId
        }
      });
      setComments(response.data?.results || response.data || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() || submittingComment) return;
    setSubmittingComment(true);
    try {
      await createComment({
        callId: callId,
        body: newComment.trim(),
        typeOfInterest: commentTypeOfInterest
      });
      setNewComment('');
      setCommentTypeOfInterest(0);
      await fetchComments();
    } catch (err) {
      console.error('Error submitting comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId);
      await fetchComments();
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  const getDeadlineStatus = (deadline) => {
    if (!deadline) return { color: 'default', text: 'Always open', icon: null };
    const deadlineDate = new Date(deadline);
    const today = new Date();
    const daysLeft = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { color: 'error', text: 'Expired', icon: <WarningIcon fontSize="small" /> };
    if (daysLeft <= 7) return { color: 'error', text: `${daysLeft} days left`, icon: <WarningIcon fontSize="small" /> };
    if (daysLeft <= 30) return { color: 'warning', text: `${daysLeft} days left`, icon: <ScheduleIcon fontSize="small" /> };
    return { color: 'success', text: `${daysLeft} days left`, icon: <CheckCircleIcon fontSize="small" /> };
  };

  const deadlineStatus = call ? getDeadlineStatus(call.closes_at) : { color: 'default', text: '', icon: null };

  const EditCallDialog = ({ open, onClose, call, onSubmit }) => {
    const [form, setForm] = useState({});
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableSkills, setAvailableSkills] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingSkills, setLoadingSkills] = useState(false);

    useEffect(() => {
      if (open && call) {
        setForm({
          title: call.title || '',
          code: call.code || '',
          scope: call.scope || '',
          expectedOutcomes: call.expected_outcomes || '',
          eligibilityCriteria: call.eligibility_criteria || '',
          typeOfAction: call.type_of_action || 'both',
          opensAt: call.opens_at ? call.opens_at.slice(0, 16) : '',
          closesAt: call.closes_at ? call.closes_at.slice(0, 16) : '',
          totalBudget: call.total_budget || '',
          maxPerProject: call.max_per_project || '',
          minPartners: call.min_partners || 2,
          status: call.status || 'upcoming',
          externalUrl: call.external_url || '',
          alwaysOpen: call.is_always_open || false,
          isAiSummary: call.is_ai_summary || false,
          categories: call.categories || [],
          skills: call.skills || [],
        });
        setError(null);
        fetchCategories();
        fetchSkills();
      }
    }, [open, call]);

    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await axiosService.get('/funding/call-categories/');
        setAvailableCategories(response.data.results || response.data || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setAvailableCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    const fetchSkills = async () => {
      setLoadingSkills(true);
      try {
        const response = await axiosService.get('/skills/');
        setAvailableSkills(response.data.results || response.data || []);
      } catch (err) {
        console.error('Error fetching skills:', err);
        setAvailableSkills([]);
      } finally {
        setLoadingSkills(false);
      }
    };

    const set = (k) => (e) => {
      const value = e?.target ? e.target.value : e;
      setForm((f) => ({ ...f, [k]: value }));
    };

    const handleSubmit = async () => {
      try {
        setSubmitting(true);
        setError(null);

        const is_always_open = form.alwaysOpen;
        const submitData = {
          title: form.title,
          code: form.code,
          scope: form.scope,
          expected_outcomes: form.expectedOutcomes,
          eligibility_criteria: form.eligibilityCriteria,
          type_of_action: form.typeOfAction,
          opens_at: is_always_open ? null : (form.opensAt || null),
          closes_at: is_always_open ? null : (form.closesAt || null),
          is_always_open,
          total_budget: form.totalBudget || null,
          max_per_project: form.maxPerProject || null,
          min_partners: form.minPartners || null,
          status: is_always_open ? 'open' : form.status,
          external_url: form.externalUrl || null,
          is_ai_summary: form.isAiSummary,
          categories: (form.categories || []).map(cat => {
            if (typeof cat === 'string') return cat;
            if (cat.isNew) return cat.inputValue;
            return cat.id;
          }),
          skills: (form.skills || []).map(skill => {
            if (typeof skill === 'string') return skill;
            if (skill.isNew) return skill.inputValue;
            return skill.id;
          }),
        };

        await onSubmit(submitData);
      } catch (err) {
        const data = err?.response?.data;
        let errorMessage;
        if (data) {
          if (typeof data === 'string') errorMessage = data;
          else if (data.detail) errorMessage = data.detail;
          else if (data.message) errorMessage = data.message;
          else errorMessage = Object.values(data).flatMap(v => Array.isArray(v) ? v : [v]).join(' ');
        }
        setError(errorMessage || err?.message || 'Failed to update call.');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" sx={{ zIndex: 1600 }} fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Call</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField size="small" label="Call Title" fullWidth value={form.title || ''} onChange={set('title')} />
            <TextField size="small" label="Call Code" fullWidth value={form.code || ''} onChange={set('code')} />
            <TextField size="small" label="External URL" fullWidth value={form.externalUrl || ''} onChange={set('externalUrl')} helperText="Link to the official call page" />
            <TextField size="small" label="Scope" fullWidth multiline rows={3} value={form.scope || ''} onChange={set('scope')} />
            <TextField size="small" label="Expected Outcomes" fullWidth multiline rows={3} value={form.expectedOutcomes || ''} onChange={set('expectedOutcomes')} />
            <TextField size="small" label="Eligibility Criteria" fullWidth multiline rows={3} value={form.eligibilityCriteria || ''} onChange={set('eligibilityCriteria')} />
            
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={availableCategories}
              getOptionLabel={(option) => typeof option === 'string' ? option : (option.name || '')}
              value={form.categories || []}
              onChange={(e, newValue) => set('categories')(newValue)}
              loading={loadingCategories}
              isOptionEqualToValue={(option, value) => {
                if (typeof option === 'string' && typeof value === 'string') return option === value;
                if (typeof option === 'object' && typeof value === 'object') return option.id === value.id;
                return false;
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter((option) =>
                  option.name.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                const { inputValue } = params;
                const isExisting = options.some((option) => inputValue.toLowerCase() === option.name.toLowerCase());
                if (inputValue !== '' && !isExisting) {
                  filtered.push({
                    inputValue,
                    name: `Add "${inputValue}"`,
                    isNew: true
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categories"
                  placeholder="Select or type new categories"
                  helperText="Choose existing or type new category names"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={typeof option === 'string' ? option : option.name}
                    size="small"
                    color={typeof option === 'string' || option.isNew ? 'secondary' : 'default'}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
            
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={availableSkills}
              getOptionLabel={(option) => typeof option === 'string' ? option : (option.name || '')}
              value={form.skills || []}
              onChange={(e, newValue) => set('skills')(newValue)}
              loading={loadingSkills}
              isOptionEqualToValue={(option, value) => {
                if (typeof option === 'string' && typeof value === 'string') return option === value;
                if (typeof option === 'object' && typeof value === 'object') return option.id === value.id;
                return false;
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter((option) =>
                  option.name.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                const { inputValue } = params;
                const isExisting = options.some((option) => inputValue.toLowerCase() === option.name.toLowerCase());
                if (inputValue !== '' && !isExisting) {
                  filtered.push({
                    inputValue,
                    name: `Add "${inputValue}"`,
                    isNew: true
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Skills"
                  placeholder="Select or type new skills"
                  helperText="Choose existing or type new skill names"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={typeof option === 'string' ? option : option.name}
                    size="small"
                    color={typeof option === 'string' || option.isNew ? 'secondary' : 'default'}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
            
            <FormControl size="small" fullWidth>
              <InputLabel>Type of Action</InputLabel>
              <Select value={form.typeOfAction || 'both'} onChange={set('typeOfAction')} label="Type of Action">
                <MenuItem value="research">Research</MenuItem>
                <MenuItem value="innovation">Innovation</MenuItem>
                <MenuItem value="ria">Research and Innovation</MenuItem>
                <MenuItem value="both">Research and Innovation</MenuItem>
                <MenuItem value="csa">Coordination and Support Action</MenuItem>
                <MenuItem value="cofund">Co-Fund Action</MenuItem>
                <MenuItem value="ppi">Public Procurement of Innovation</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={form.status || 'upcoming'} onChange={set('status')} label="Status">
                <MenuItem value="upcoming">Upcoming</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Checkbox checked={form.alwaysOpen || false} onChange={(e) => set('alwaysOpen')(e.target.checked)} />}
              label="Always Open"
            />
            <FormControlLabel
              control={<Checkbox checked={form.isAiSummary || false} onChange={(e) => set('isAiSummary')(e.target.checked)} />}
              label="AI-Generated Summary"
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <ProfileDateTimePicker label="Opens At" size="small" value={form.opensAt || null} onChange={set('opensAt')} disabled={form.alwaysOpen} />
              <ProfileDateTimePicker label="Closes At" size="small" value={form.closesAt || null} onChange={set('closesAt')} disabled={form.alwaysOpen} defaultTime="17:00" />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField size="small" label="Total Budget" type="number" fullWidth value={form.totalBudget || ''} onChange={set('totalBudget')} />
              <TextField size="small" label="Max Per Project" type="number" fullWidth value={form.maxPerProject || ''} onChange={set('maxPerProject')} />
            </Box>
            <TextField size="small" label="Min Partners" type="number" fullWidth value={form.minPartners || ''} onChange={set('minPartners')} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={48} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4, borderRadius: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!call) {
    return (
      <Container maxWidth="lg">
        <Alert severity="warning" sx={{ mt: 4, borderRadius: 2 }}>
          Call not found.
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
       
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <Header elevation={0} sx={{ bgcolor: theme.palette.background.paper }}>
        <Box sx={{ maxWidth: 1400, px: { xs: 2, sm: 3 }, 
              py: { xs: 1.5, sm: 2 }, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <BackButton />
          <Typography variant="h4" fontWeight={800} sx={{ flex: 1, letterSpacing: 0.2, background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`, backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent' }}>
            Call Details

            <Tooltip title="Show Call Details guide" placement="bottom">
            <IconButton onClick={guide.show} size="small"  aria-label="Show Call Details guide" 
              sx={{ ml: 1,  transition: 'all 0.2s ease', '&:hover': { transform: 'scale(1.05)' } }}>
              <HelpOutlineIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
          </Tooltip>

          </Typography>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', flexWrap: 'wrap' }}>
            {stepChips}
          </Box>

          <Tooltip title={isPinned ? 'Pinned to Quick Access' : 'Pin to Quick Access'}>
            <IconButton onClick={handlePinCall} aria-label="Pin to Quick Access" sx={{ transition: 'all 0.2s ease', '&:hover': { transform: 'scale(1.05)' } }}>
              {isPinned ? <PinIcon color="primary" /> : <PinOutlinedIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Header>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={11} lg={9}>
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, 
                      background: theme.palette.background.paper 
                  
                  }}>
              <Stack ref={sectionsRef} spacing={3}>
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                    <Box display="flex" alignItems="center" gap={1} sx={{ flexWrap: 'wrap' }}>
                      <StyledChip 
                        label={call.status || 'Open'}
                        size="small"
                        color={call.status === 'open' ? 'success' : call.status === 'closed' ? 'error' : 'default'}
                        sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                      />
                      {call.agency?.abbreviation && (
                        <StyledChip label={call.agency.abbreviation} size="small" variant="outlined" />
                      )}
                      {call.is_single_stage !== undefined && (
                        <StyledChip label={call.is_single_stage === 'single' ? 'Single Stage' : call.is_single_stage === 'two-stage' ? 'Two Stage' : 'Both'} size="small" variant="outlined" />
                      )}
                      <StyledChip
                        label={deadlineStatus.text}
                        color={deadlineStatus.color}
                        size="small"
                        icon={deadlineStatus.icon}
                      />
                    </Box>
                    {(call.is_ai_summary || SYNC_SOURCES[call.source]) && (
                    <Tooltip
                      title={
                        <Box sx={{ p: 1, maxWidth: 400 }}>
                          <Typography
                            variant="body2"
                            sx={{ lineHeight: 1.6, fontWeight: 600, mb: 1, textAlign: 'center' }}
                          >
                            DISCLAIMER
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ lineHeight: 1.6, textAlign: 'justify' }}
                          >
                            {call.is_ai_summary ? (
                              <>This page contains an AI-generated summary of the original Call for Proposals.
                              It is meant solely as an informational resource to assist in consortium formation
                              and proposal writing.</>
                            ) : (
                              <>This call was copied automatically from {SYNC_SOURCES[call.source]}
                              {call.last_synced_at ? `, last checked on ${new Date(call.last_synced_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}.
                              Its categories and skills, and the researchers suggested for it, are chosen by AI.</>
                            )}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ mt: 1.5, lineHeight: 1.6, textAlign: 'justify' }}
                          >
                            Please refer to the official Call for Proposals document for complete and authoritative
                            details, including eligibility criteria, application requirements, and submission deadlines.
                            The original document serves as the final reference in all matters of interpretation and application.
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="left"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
                            '& .MuiTooltip-arrow': {
                              color: 'background.paper',
                              '&::before': {
                                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                              }
                            }
                          }
                        }
                      }}
                    >
                      <StyledChip
                        label={call.is_ai_summary ? 'Disclaimer: AI-Generated Summary' : 'Disclaimer: Imported automatically, AI-tagged'}
                        size="small"
                        color="warning"
                        variant="outlined"
                        icon={<WarningIcon />}
                        sx={{
                          fontWeight: 600,
                          cursor: 'help',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.warning.main, 0.08),
                          }
                        }}
                      />
                    </Tooltip>
                    )}
                  </Box>

                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, letterSpacing: -0.5 }}>
                        {call.code }
                        <FollowButton
                          contentType="call"
                          objectId={call.id}
                          size="medium"
                          variant="outlined"
                          sx={{ ml: 2 }}
                        />
                      </Typography>
                      {call.title && (
                        <Typography variant="subtitle1"  sx={{ mb: 0, lineHeight: 1.4, fontWeight: 600 }}>
                          {call.title}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" gap={1}>
                      <ReportButton
                        targetType="fundingcall"
                        targetId={call?.id}
                        targetLabel={call?.title}
                        size="small"
                      />
                      {(currentUser?.is_superuser || String(currentUser?.id) === String(call?.created_by)) && (
                        <Tooltip title="Edit Call">
                          <IconButton color="primary" onClick={() => setEditDialogOpen(true)} size="small" sx={{ transition: 'all 0.2s ease', '&:hover': { transform: 'scale(1.05)' } }}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(call.external_url || call.data_source_url) && (
                        <Tooltip title="View Official Call Page">
                          <IconButton component="a" href={call.external_url || call.data_source_url} target="_blank" rel="noopener noreferrer" size="small" sx={{ transition: 'all 0.2s ease', '&:hover': { transform: 'scale(1.05)' } }}>
                            <LaunchIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>




                  

<Grid container spacing={3}>
      {call.opens_at && (
        <Grid item xs={12} sm={6} md={3}>
    
    <InfoCard>
      <Box display="flex" alignItems="flex-start" gap={1}>
        <EventIcon sx={{ fontSize: 24, mt: 0.3,  color: 'primary.main' }} />
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase',  mr: 1 }}>
            Opens at
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, }}>
            {new Date(call.opens_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          </Typography>
        </Box>
      </Box>
  </InfoCard>

        </Grid>
      )}
      {call.closes_at && (
        <Grid item xs={12} sm={6} md={3}>
          <InfoCard>
          <Box display="flex" alignItems="flex-start" gap={1}>
              <EventIcon sx={{ fontSize: 24, mt: 0.3, color: deadlineStatus.color === 'error' ? 'error.main' : deadlineStatus.color === 'warning' ? 'warning.main' : 'success.main' }} />
              <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Closes at
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {new Date(call.closes_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
            </Typography>
            </Box>
            </Box>
         
          </InfoCard>
        </Grid>
      )}
      {call.deadline2 && (
        <Grid item xs={12} sm={6} md={3}>
          <InfoCard>
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0 }}>
              <EventIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Deadline 2
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {new Date(call.deadline2).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({call.timezone || 'Brussels Time'})
              </Typography>
            </Typography>
          </InfoCard>
        </Grid>
      )}
      {call.total_budget && (
        <Grid item xs={12} sm={6} md={3.5}>
        

        <InfoCard sx={{ p: 1.5, py:2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '.5fr auto auto', gap: 1, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
              <Typography sx={{ fontSize: 24, color: 'primary.main', lineHeight: 1 }}>💰</Typography>
            </Box>

            <Box sx={{ gridColumn: 'span auto' }}>
              {/* <Typography variant="caption" sx={{ fontSize: '1rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', textAlign: 'center', display: 'block', mb: 1.5 }}>
                Budget
              </Typography> */}
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600,   textTransform: 'uppercase', color: 'text.secondary', fontSize: '.9rem', textAlign: 'center', lineHeight: 1.5 }}>
                  Per Project
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600,  textTransform: 'uppercase', color: 'text.secondary', fontSize: '.9rem', textAlign: 'center', lineHeight: 1.5 }}>
                  Total
                </Typography>
                
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'center', lineHeight: 1.3 }}>
                  {formatCompactMoney(call.max_per_project, call.currency) || '—'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'center', lineHeight: 1.3 }}>
                  {formatCompactMoney(call.total_budget, call.currency) || '—'}
                </Typography>
              </Box>
            </Box>
          </Box>
</InfoCard>


        
        </Grid>
      )}

    </Grid>



                  {introOnly(call.description, call) ? (
                    <CallText sx={{ mb: 1, mt: 2 }}>{introOnly(call.description, call)}</CallText>
                  ) : !(call.scope || call.expected_outcomes || call.eligibility_criteria) && (
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1, mt: 2, lineHeight: 1.6 }}>{call.title}</Typography>
                  )}
                  <Button
                    size="small"
                    endIcon={detailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setDetailsExpanded(!detailsExpanded)}
                    sx={{ 
                      textTransform: 'none', 
                      color: 'primary.main',
                      fontWeight: 600,
                      p: 0,
                      '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                    }}
                  >
                    {detailsExpanded ? 'Show less' : 'More details'}
                  </Button>

                  <Collapse in={detailsExpanded}>
                    <Stack spacing={3} sx={{ mt: 3 }}>
                      {call.scope && (
                        <Box>
                          <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: '12px', bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <DescriptionIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Scope
                            </Typography>
                          </Box>
                          <CallText sx={{ pl: 6 }}>{call.scope}</CallText>
                        </Box>
                      )}

                      {call.expected_outcomes && (
                        <Box>
                          <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: '12px', bgcolor: alpha(theme.palette.success.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <TrendingUpIcon sx={{ fontSize: 18, color: 'success.main' }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Expected Outcomes
                            </Typography>
                          </Box>
                          
                         
                          <CallText sx={{ pl: 6 }}>{call.expected_outcomes}</CallText>

                          
                        </Box>
                      )}

                      {call.eligibility_criteria && (
                        <Box>
                          <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: '12px', bgcolor: alpha(theme.palette.info.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <SchoolIcon sx={{ fontSize: 18, color: 'info.main' }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Eligibility Criteria
                            </Typography>
                          </Box>
                          
                          <CallText sx={{ pl: 6 }}>{call.eligibility_criteria}</CallText>

                          
                        </Box>
                      )}

                      {call.type_of_action && (
                        <Box>
                          <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 0 }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: '12px', bgcolor: alpha(theme.palette.warning.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <ScienceIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Type of Action
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, pl: 6 }}>
                            {getActionTypeDisplay(call.type_of_action)}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Collapse>
                </Box>

    
                {call.categories && call.categories.length > 0 && (
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5, mt: 1 }}>
                      <CategoryIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Categories
                      </Typography>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {call.categories.map((category, idx) => (
                        <StyledChip
                          key={idx}
                          label={category.name}
                          size="small"
                          sx={{ 
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: 'primary.main',
                            fontWeight: 500,
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {call.skills && call.skills.length > 0 && (
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        🔧 Skills
                      </Typography>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {call.skills.map((skill, idx) => (
                        <StyledChip
                          key={idx}
                          label={skill.name}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}





              </Stack>

              <GradientDivider sx={{ my: 3 }} />

              <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={1} sx={{ bgcolor: alpha(theme.palette.grey[100], 0.8), borderRadius: 3, p: 1, width: 'fit-content' }}>
                  <LikeButton
                    contentType="call"
                    contentId={call.id}
                    currentUser={currentUser}
                    size="small"
                  />
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <Tooltip title="Suggest to Colleague">
                    <IconButton
                      sx={{ color: 'secondary.main', transition: 'all 0.2s ease', '&:hover': { transform: 'scale(1.05)' } }}
                      onClick={handleSuggestClick}
                      disabled={loadingRecommendations}
                      size="small"
                    >
                      {loadingRecommendations ? <CircularProgress size={20} /> : <SendIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Build Consortium">
                    <IconButton
                      color="primary"
                      onClick={handleConsortiumClick}
                      disabled={loadingRecommendations}
                      size="small"
                      sx={{ transition: 'all 0.2s ease', '&:hover': { transform: 'scale(1.05)' } }}
                    >
                      {loadingRecommendations ? <CircularProgress size={20} /> : <GroupIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Schedule a Meeting">
                    <IconButton
                      color="info"
                      onClick={() => setMeetingDialogOpen(true)}
                      size="small"
                      sx={{ transition: 'all 0.2s ease', '&:hover': { transform: 'scale(1.05)' } }}
                    >
                      <EventIcon />
                    </IconButton>
                  </Tooltip>
                </Box>


                {suggestedExperts && (
                      <Button
                        size="medium"
                        // variant="outlined"
                        // endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate(`/funding/call/${callId}/experts`)}
                        sx={{ textTransform: 'none' }}
                      >
                        Suggested Experts  ({suggestedExperts.length})
                      </Button>
                    )}

                <Button
                  startIcon={<CommentIcon />}
                  onClick={() => setCommentsExpanded(!commentsExpanded)}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    color: 'text.secondary',
                    borderRadius: 2,
                    '&:hover': { bgcolor: alpha(theme.palette.grey[100], 0.8) }
                  }}
                >
                  Comments {commentCount > 0 && <Chip label={commentCount} size="small" sx={{ ml: 1, height: 20, fontWeight: 600 }} />}
                </Button>

             
              </Box>

         


              <Collapse in={commentsExpanded}>
                <Box sx={{ mt: 3 }}>
                  <CallComments
                    targetId={call.id}
                    targetType="call"
                    currentUser={currentUser}
                    onCommentCountChange={setCommentCount}
                    canModerate={
                      currentUser?.is_superuser ||
                      currentUser?.is_staff ||
                      String(currentUser?.id) === String(call?.created_by)
                    }
                    composerRef={commentComposerRef}
                  />
                </Box>
              </Collapse>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4} lg={2.8}>
            <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 } }}>
              <SidebarCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                      <DiscoverIcon sx={{ mr: 1, color: 'primary.light' }} />
                      Discover & Follow
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={fetchSuggestionsToFollowData}
                      disabled={loadingSuggestions}
                      title="Refresh suggestions"
                      sx={{
                        color: 'primary.main',
                        '&:hover': { color: 'primary.dark' },
                      }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {loadingSuggestions ? (
                    <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                      <LinearProgress sx={{ width: '100%', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">Loading suggestions...</Typography>
                    </Box>
                  ) : suggestionsToFollow.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No suggestions available at the moment.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {suggestionsToFollow.slice(0, nSuggestionsToFollow).map((suggestion, i) => (
                        <Box key={suggestion.id ?? i} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                          <EntityLink
                            type={suggestion.content_type}
                            id={suggestion.object_id}
                            name={suggestion.name}
                            description={
                              suggestion.content_type === 'fundingprogram'
                                ? 'Funding program' : suggestion.description
                            }
                            showAvatar={true}
                            variant="body2"
                            color="text.primary"
                            underline="none"
                            avatar={{
                              src: suggestion.avatar,
                              alt: suggestion.name?.charAt(0) || suggestion.type?.charAt(0),
                              children: suggestion.name?.charAt(0) || suggestion.type?.charAt(0),
                              sx: {
                                backgroundColor: theme.palette.primary.avatar2,
                                color: theme.palette.primary.contrastText,
                              },
                            }}
                          />
                          <FollowButton
                            contentType={suggestion.content_type}
                            objectId={suggestion.object_id}
                            size="small"
                            variant="outlined"
                            compact
                          />
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </SidebarCard>

              {/* Suggested Experts Section */}
              <SidebarCard>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                      <ScienceIcon sx={{ mr: 1, color: 'primary.main' }} />
                      Suggested Experts
                    </Typography>
                    {suggestedExperts.length > 5 && (
                      <Button
                        size="small"
                        // endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate(`/funding/call/${callId}/experts`)}
                        sx={{ textTransform: 'none' }}
                      >
                        Show All ({suggestedExperts.length})
                      </Button>
                    )}
                  </Box>
                  {loadingExperts ? (
                    <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                      <LinearProgress sx={{ width: '100%', mb: 1, borderRadius: 1 }} />
                      <Typography variant="body2" color="text.secondary">Finding experts...</Typography>
                    </Box>
                  ) : suggestedExperts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                      No matching experts found yet.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {suggestedExperts.slice(0, 5).map((expert, i) => (
                        <Box 
                          key={expert.id || i} 
                          display="flex" 
                          alignItems="center" 
                          justifyContent="space-between"
                          sx={{ 
                            cursor: 'pointer',
                            p: 1,
                            borderRadius: 1,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                            }
                          }}
                          onClick={() => navigate(`/profile/${expert.id}`)}
                        >
                          <Box display="flex" alignItems="center" gap={1.5} flex={1}>
                            <Avatar
                              src={expert.avatar || expert.profile_image}
                              alt={`${expert.first_name} ${expert.last_name}`}
                              sx={{ width: 36, height: 36,
                                fontSize: '1.5rem',
                                backgroundColor: theme.palette.primary.avatar,
                                color: 'white',
                               }}
                            />
                            <Box flex={1}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {expert.first_name} {expert.last_name}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                {expert.skill_matches > 0 && (
                                  <Chip 
                                    label={`${expert.skill_matches} skill(s)`} 
                                    size="small"
                                    color="primary"
                                    sx={{ height: 18, fontSize: '0.9rem', fontWeight: 500 }}
                                  />
                                )}
                                {expert.interest_matches > 0 && (
                                  <Chip 
                                    label={`${expert.interest_matches} interest(s)`} 
                                    size="small"
                                    color="secondary"
                                    sx={{ height: 18, fontSize: '0.9rem', fontWeight: 500, color: '#fff' }}
                                  />
                                )}
                                {/* {expert.avg_proficiency && (
                                  <Chip 
                                    label={`Level ${expert.avg_proficiency.toFixed(1)}`} 
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.7rem' }}
                                  />
                                )} */}
                                {/* {expert.avg_weight && (
                                  <Chip 
                                    label={`Int ${expert.avg_weight.toFixed(1)}/10`} 
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.7rem' }}
                                  />
                                )} */}
                              </Box>
                            </Box>
                          </Box>
                          {/* <StarIcon sx={{ fontSize: 18, color: 'warning.main', opacity: 0.8 }} /> */}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </SidebarCard>

              {/* Suggested Experts now includes both skill and interest matches */}
            </Box>
          </Grid>
        </Grid>
      </Container>

      {guide.open && (
        <GuideTour
          steps={CALL_DETAIL_GUIDE_STEPS}
          step={guide.step}
          targetRect={guide.targetRect}
          onNext={guide.next}
          onClose={guide.close}
          ariaLabel="Call Details guide"
        />
      )}

      <InviteToSuggestEntityDialog
        open={suggestDialogOpen}
        onClose={() => setSuggestDialogOpen(false)}
        entityType="call"
        entity={call}
        currentUserId={currentUser?.id}
        mode="suggest"
      />

      <ConsortiumDialog 
        open={consortiumDialogOpen} 
        onClose={() => setConsortiumDialogOpen(false)} 
        call={call}
        potentialMembers={potentialMembers}
        loading={loadingRecommendations}
      />
      
      <MeetingScheduleDialog 
        open={meetingDialogOpen} 
        onClose={() => setMeetingDialogOpen(false)} 
        call={call}
      />

      <EditCallDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        call={call}
        onSubmit={handleEditCall}
      />
    </Box>
  );
};

export default CallDetailPage;